import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AnimatedHero } from '@features/home/components';
import { Seo } from '@components/Seo';
import { pageGraph } from '@lib/schema';
import { HOME_CATEGORIES, HOME_PAGE, HOME_SECONDARY } from '@/content/pages.js';
import { SERVICE_CONTENT } from '@/content/services.js';
import { SERVICE_TERRITORY } from '@lib/businessContact';

function serviceByHref(href) {
  return Object.values(SERVICE_CONTENT).find(service => service.path === href) || null;
}

const PROJECT_THUMBS = {
  '/projects/sky-zone-facade-lighting': {
    src: '/images/projects/sky-zone-facade-after-blue-violet-lighting.jpg',
    webp: '/images/projects/sky-zone-facade-after-blue-violet-lighting.webp',
    alt: 'Sky Zone facade after blue and violet lighting',
    width: 1536,
    height: 2048
  }
};

function visualForHref(href) {
  const service = serviceByHref(href);
  return service?.illustration || service?.images?.[0] || PROJECT_THUMBS[href] || null;
}

function ServiceThumb({ image, label }) {
  if (!image) return null;
  return (
    <picture>
      {image.webp && <source srcSet={image.webp} type="image/webp" />}
      <img
        src={image.src}
        alt={image.alt || label}
        width={image.width || 1280}
        height={image.height || 720}
        loading="lazy"
        decoding="async"
        className="w-full h-40 object-cover"
      />
    </picture>
  );
}

ServiceThumb.propTypes = {
  image: PropTypes.object,
  label: PropTypes.string
};

export function Home({ introComplete, heroImage, heroImageSecondary }) {
  return (
    <>
      <Seo routeKey="home" jsonLd={pageGraph({ path: '/', title: HOME_PAGE.title, description: HOME_PAGE.description })} />
      <AnimatedHero
        heroImage={heroImage}
        heroImageSecondary={heroImageSecondary}
        introComplete={introComplete}
      />

      <section className="py-12 sm:py-20 bg-[#0F0F23] relative z-10">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What Avision Lighting installs</h2>
          <p className="text-white/75 max-w-3xl text-lg mb-10">{HOME_PAGE.lede}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {HOME_CATEGORIES.map(category => {
              const visual = visualForHref(category.href);
              return (
                <Link
                  key={category.href}
                  to={category.href}
                  className="glass-card overflow-hidden border border-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-colors"
                >
                  <ServiceThumb image={visual} label={category.label} />
                  <div className="p-6 sm:p-8">
                    <h3 className="text-xl font-bold text-white mb-3">{category.label}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{category.text}</p>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {HOME_SECONDARY.map(link => {
              const visual = visualForHref(link.href);
              if (!visual) {
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[#06B6D4] font-semibold hover:underline py-2"
                  >
                    {link.label}
                  </Link>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className="glass-card overflow-hidden border border-white/10 hover:border-[#06B6D4]/40 transition-colors"
                >
                  <ServiceThumb image={visual} label={link.label} />
                  <div className="p-4">
                    <span className="text-white font-semibold text-sm">{link.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1E1B4B] border-y border-white/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-white mb-3">Featured work</h2>
          <p className="text-white/75 mb-8 max-w-3xl">
            Before-and-after photography from a Sky Zone facade lighting install — white
            architectural wash replaced with blue and violet color.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <figure>
              <picture>
                <source srcSet="/images/projects/sky-zone-facade-before-white-lighting.webp" type="image/webp" />
                <img
                  src="/images/projects/sky-zone-facade-before-white-lighting.jpg"
                  alt="Sky Zone entrance at night with white architectural lighting on the facade arches"
                  width="1536"
                  height="2048"
                  loading="lazy"
                  decoding="async"
                  className="w-full max-h-[420px] object-cover object-[center_28%] rounded-2xl border border-white/10"
                />
              </picture>
              <figcaption className="mt-2 text-sm text-white/60">Before: white architectural wash</figcaption>
            </figure>
            <figure>
              <picture>
                <source srcSet="/images/projects/sky-zone-facade-after-blue-violet-lighting.webp" type="image/webp" />
                <img
                  src="/images/projects/sky-zone-facade-after-blue-violet-lighting.jpg"
                  alt="Blue and violet facade lighting on a Sky Zone entrance at night"
                  width="1536"
                  height="2048"
                  loading="lazy"
                  decoding="async"
                  className="w-full max-h-[420px] object-cover object-[center_28%] rounded-2xl border border-white/10"
                />
              </picture>
              <figcaption className="mt-2 text-sm text-white/60">After: blue and violet wash</figcaption>
            </figure>
          </div>
          <Link to="/projects/sky-zone-facade-lighting" className="text-[#06B6D4] font-semibold hover:underline">
            Read the Sky Zone facade project
          </Link>
          {' · '}
          <Link to="/gallery" className="text-[#06B6D4] font-semibold hover:underline">
            Open the project gallery
          </Link>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-[#0F0F23]">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                South Florida first
              </h2>
              <p className="text-white/70 mb-4">
                Based in West Palm Beach, with installation across{' '}
                {SERVICE_TERRITORY.primaryCounties.join(', ')}. There is no public showroom. The
                crew works at the venue.
              </p>
              <p className="text-white/60 text-sm mb-6">{SERVICE_TERRITORY.secondaryNote}</p>
              <Link to="/service-area" className="text-[#06B6D4] font-semibold hover:underline">
                South Florida service area
              </Link>
            </div>
            <div className="glass-card border border-[#7C3AED]/25 p-6 sm:p-8">
              <p className="text-[#06B6D4] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                {SERVICE_TERRITORY.nationwideLabel}
              </p>
              <h3 className="text-xl font-bold text-white mb-3">Selective national reach</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                {SERVICE_TERRITORY.nationwideNote}
              </p>
              <Link to="/contact" className="text-[#06B6D4] font-semibold hover:underline text-sm">
                Discuss a large-scale project
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-[#1E1B4B] text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-6">Request a lighting assessment</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us the facility, the county, and whether the work is RGBW, a retrofit, sports
            lighting, or a facade.
          </p>
          <Link to="/contact" className="btn-primary px-8 py-3">
            Request a Lighting Assessment
          </Link>
        </div>
      </section>
    </>
  );
}

Home.propTypes = {
  introComplete: PropTypes.bool,
  heroImage: PropTypes.string.isRequired,
  heroImageSecondary: PropTypes.string
};
